import { useState, useEffect } from 'react';
import { 
  TimelinePost, 
  timelineService, 
  TimelineQueryParams, 
  CreateTimelinePost, 
  UpdateTimelinePost 
} from '@/services/timeline';
import { PaginatedResponse } from '@/types/common';
import { useToast } from '@/hooks/use-toast';

export const useTimeline = (params: TimelineQueryParams) => {
  const [data, setData] = useState<PaginatedResponse<TimelinePost> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchTimeline = async (fetchParams?: TimelineQueryParams) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await timelineService.getAll(fetchParams || params);
      setData(response);
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao carregar timeline';
      setError(errorMessage);
      toast({
        variant: 'error',
        title: 'Erro ao carregar timeline',
        description: errorMessage,
      });
      console.error('Error fetching timeline:', err);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const createPost = async (postData: CreateTimelinePost) => {
    try {
      const newPost = await timelineService.create(postData);
      toast({
        variant: 'success',
        title: 'Post criado com sucesso',
      });
      await fetchTimeline();
      return newPost;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao criar post';
      toast({
        variant: 'error',
        title: 'Erro ao criar post',
        description: errorMessage,
      });
      throw err;
    }
  };

  const updatePost = async (postId: string, postData: UpdateTimelinePost) => {
    try {
      const updatedPost = await timelineService.update(postId, postData);
      toast({
        variant: 'success',
        title: 'Post atualizado com sucesso',
      });
      await fetchTimeline();
      return updatedPost;
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao atualizar post';
      toast({
        variant: 'error',
        title: 'Erro ao atualizar post',
        description: errorMessage,
      });
      throw err;
    }
  };

  const pinPost = async (postId: string) => {
    try {
      await timelineService.pin(postId);
      toast({
        variant: 'success',
        title: 'Post fixado com sucesso',
      });
      await fetchTimeline();
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao fixar post';
      toast({
        variant: 'error',
        title: 'Erro ao fixar post',
        description: errorMessage,
      });
      throw err;
    }
  };

  const favoritePost = async (postId: string) => {
    try {
      await timelineService.favorite(postId);
      toast({
        variant: 'success',
        title: 'Post favoritado com sucesso',
      });
      await fetchTimeline();
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao favoritar post';
      toast({
        variant: 'error',
        title: 'Erro ao favoritar post',
        description: errorMessage,
      });
      throw err;
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, [params.type, params.offset, params.limit, params.q, params.territories, params.contexts, params.topics]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchTimeline,
    createPost,
    updatePost,
    pinPost,
    favoritePost,
  };
};

